import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  FileToUpload,
  uploadFile,
  uploadFileToCollection,
  UploadingStatus,
} from "api/media-library";
import { updateAsset } from "api/media-library/update-asset";
import { FileWithURL } from "api/media-library/types";
import { CollectionFile } from "shared/types/files";
import { ReduxRootState } from "@/types/redux-store";

export type { FileToUpload } from "api/media-library";

export interface UploadingFile {
  id: string;
  progress: {
    value: number;
    status: UploadingStatus;
  };
  file: FileToUpload;
  error?: unknown;
}

export type UploadingMode = "parallel" | "subsequent";

const featureKey = "mediaPickerUploads" as const;

const uploadingFilesAdapter = createEntityAdapter<UploadingFile, string>({
  selectId: (upload) => upload.id,
});

const uploadingFilesSlice = createSlice({
  name: featureKey,
  initialState: uploadingFilesAdapter.getInitialState(),
  reducers: {
    uploadsQueued(state, action: PayloadAction<UploadingFile[]>) {
      uploadingFilesAdapter.upsertMany(state, action.payload);
    },
    uploadProgressUpdated(
      state,
      action: PayloadAction<{
        id: string;
        progress: UploadingFile["progress"];
      }>
    ) {
      uploadingFilesAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          progress: action.payload.progress,
        },
      });
    },
    uploadFailed(
      state,
      action: PayloadAction<{
        id: string;
        error: unknown;
      }>
    ) {
      uploadingFilesAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          error: action.payload.error,
          progress: {
            value: 100,
            status: UploadingStatus.error,
          },
        },
      });
    },
    uploadSucceeded(state, action: PayloadAction<string>) {
      uploadingFilesAdapter.updateOne(state, {
        id: action.payload,
        changes: {
          progress: {
            value: 100,
            status: UploadingStatus.success,
          },
        },
      });
    },
    clearUploads(state, action: PayloadAction<string[] | undefined>) {
      if (!action.payload) {
        uploadingFilesAdapter.removeAll(state);
        return;
      }

      uploadingFilesAdapter.removeMany(state, action.payload);
    },
  },
});

export const uploadingFilesReducer = uploadingFilesSlice.reducer;
export const uploadingFilesActions = uploadingFilesSlice.actions;
export type UploadingFilesState = ReturnType<typeof uploadingFilesReducer>;

function getUploadsState(state: ReduxRootState) {
  return (
    (
      state as ReduxRootState & {
        [featureKey]?: ReturnType<typeof uploadingFilesReducer>;
      }
    )[featureKey] ?? uploadingFilesAdapter.getInitialState()
  );
}

const adapterSelectors = uploadingFilesAdapter.getSelectors(getUploadsState);

export const selectUploadingFileById = adapterSelectors.selectById;
export const selectAllUploadingFiles = adapterSelectors.selectAll;
const selectUploadingFileEntities = adapterSelectors.selectEntities;
const uploadsByIdsCache = new Map<
  string,
  {
    ids: string[];
    uploads: Array<UploadingFile | undefined>;
    result: UploadingFile[];
  }
>();

export function selectUploadingFilesByIds(
  state: ReduxRootState,
  ids: string[]
): UploadingFile[] {
  const entities = selectUploadingFileEntities(state);
  const uploads = ids.map((id) => entities[id]);
  const cacheKey = ids.join("\0");
  const cached = uploadsByIdsCache.get(cacheKey);

  if (
    cached &&
    cached.ids.length === ids.length &&
    cached.ids.every((id, index) => id === ids[index]) &&
    cached.uploads.length === uploads.length &&
    cached.uploads.every((upload, index) => upload === uploads[index])
  ) {
    return cached.result;
  }

  const result = uploads.filter(Boolean) as UploadingFile[];
  uploadsByIdsCache.set(cacheKey, {
    ids: [...ids],
    uploads,
    result,
  });

  return result;
}

let lastId = 0;
function getUniqId() {
  return `${lastId++}`;
}

function getUploadError(error: unknown) {
  if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "data" in error.response &&
    error.response.data &&
    typeof error.response.data === "object" &&
    "message" in error.response.data
  ) {
    return error.response.data.message;
  }

  return error;
}

async function uploadSingleFile({
  collectionId,
  upload,
  dispatch,
  onFileUploaded,
}: {
  collectionId: string;
  upload: { id: string; file: FileToUpload };
  dispatch: (action: unknown) => unknown;
  onFileUploaded(uploaded: CollectionFile): void;
}) {
  try {
    const intermediate = await uploadFile(upload.file, {
      onProgress({ value, status }) {
        dispatch(
          uploadingFilesActions.uploadProgressUpdated({
            id: upload.id,
            progress: {
              value,
              status,
            },
          })
        );
      },
    });

    const uploaded = await uploadFileToCollection({
      collectionId,
      file: { ...intermediate, name: upload.file.name },
    });

    if ((upload.file as FileWithURL).contentType === "url") {
      const fileWithURL = upload.file as FileWithURL;

      if (
        (fileWithURL.tag ? fileWithURL.tag.length > 0 : false) ||
        fileWithURL.description
      ) {
        const putObject = {
          description: fileWithURL.description,
          tags: fileWithURL.tag,
        };

        uploaded.description = fileWithURL.description
          ? fileWithURL.description
          : uploaded.description;
        uploaded.tags =
          fileWithURL.tag && fileWithURL.tag.length > 0
            ? fileWithURL.tag
            : uploaded.tags;

        await updateAsset(uploaded.asset_id, putObject);
      }
    }

    dispatch(uploadingFilesActions.uploadSucceeded(upload.id));
    onFileUploaded(uploaded);
  } catch (error) {
    dispatch(
      uploadingFilesActions.uploadFailed({
        id: upload.id,
        error: getUploadError(error),
      })
    );
  }
}

export function startUploadsToCollection({
  collectionId,
  files,
  onFileUploaded,
  uploadingMode,
}: {
  collectionId: string;
  files: FileToUpload[];
  onFileUploaded(uploaded: CollectionFile): void;
  uploadingMode: UploadingMode;
}) {
  return async (dispatch: (action: unknown) => unknown) => {
    const uploads = files.map((file) => ({
      id: getUniqId(),
      file,
    }));

    dispatch(
      uploadingFilesActions.uploadsQueued(
        uploads.map((upload) => ({
          id: upload.id,
          file: upload.file,
          progress: {
            value: 0,
            status: UploadingStatus.pending,
          },
        }))
      )
    );

    const runUpload = async (upload: { id: string; file: FileToUpload }) =>
      uploadSingleFile({
        collectionId,
        upload,
        dispatch,
        onFileUploaded,
      });

    if (uploadingMode === "parallel") {
      void Promise.all(uploads.map((upload) => runUpload(upload)));
    } else {
      void (async () => {
        for (const upload of uploads) {
          await runUpload(upload);
        }
      })();
    }

    return uploads.map((upload) => upload.id);
  };
}

export function isUploadComplete(upload: UploadingFile): boolean {
  return (
    upload.progress.status === UploadingStatus.success ||
    upload.progress.status === UploadingStatus.error
  );
}

export type UploadingProgress = "Uploading" | "Transcoding" | null;

export function computeUploadingProgressValue(
  uploads: UploadingFile[]
): number | null {
  const incompleteUploads = uploads.filter(
    (upload) => !isUploadComplete(upload)
  );

  if (incompleteUploads.length === 0) {
    return null;
  }

  const totalProgress = uploads.reduce(
    (sum, upload) => sum + upload.progress.value,
    0
  );

  return Math.round(totalProgress / uploads.length);
}

export function computeUploadingProgress(
  uploads: UploadingFile[]
): UploadingProgress {
  const incompleteUploads = uploads.filter(
    (upload) => !isUploadComplete(upload)
  );

  if (
    incompleteUploads.some(
      (upload) =>
        upload.progress.status === UploadingStatus.pending ||
        upload.progress.status === UploadingStatus.uploading
    )
  ) {
    return "Uploading";
  }

  if (
    incompleteUploads.some(
      (upload) => upload.progress.status === UploadingStatus.transcoding
    )
  ) {
    return "Transcoding";
  }

  return null;
}
